<div class="twilio-config">
  <mat-card class="twilio-config__card">
    <mat-card-header>
      <mat-card-title>Twilio Configuration</mat-card-title>
      <mat-card-subtitle>Configure the phone numbers this client can call and text from</mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <mat-spinner *ngIf="isLoading" class="twilio-config__spinner" diameter="32"></mat-spinner>

      <div *ngIf="!isLoading && hasPersistedEntries" class="twilio-config__notice">
        <mat-icon class="twilio-config__notice-icon">info</mat-icon>
        <span>
          This client calls from one number. Adding a number, or switching the active one, retires the previous
          one automatically — it moves to Previous numbers rather than disappearing, so call history keeps pointing
          at the number the clinic actually used. Saved numbers cannot be edited in place: a number identifies its
          line for Twilio and for inbound routing.
        </span>
      </div>

      <form *ngIf="!isLoading" [formGroup]="configForm" (ngSubmit)="onSubmit()">
        <div formArrayName="entries">
          <!--
            Two passes over the same control array, not two arrays. `formGroupName` must receive the
            index within `entries.controls`; a position inside a filtered sublist would still render
            and still compile, and would write to the wrong entry on save.

            An unsaved row stays in this first pass whatever its `isActive` is. Adding a second row
            retires the first, and when the split keyed on `isActive` alone that half-typed row moved
            into a section collapsed by default — it vanished from the screen while still holding a
            `required` validator, so Save greyed out with the offending field nowhere in sight.
          -->
          <ng-container *ngFor="let entry of entries.controls; let i = index">
            <ng-container *ngIf="entry.get('isActive')?.value || !isPersisted(i)">
              <ng-container *ngTemplateOutlet="phoneRow; context: { $implicit: entry, index: i }"></ng-container>
            </ng-container>
          </ng-container>

          <div *ngIf="retiredCount" class="twilio-config__history">
            <button
              mat-button
              type="button"
              class="twilio-config__history-toggle"
              [attr.aria-expanded]="showRetired"
              (click)="showRetired = !showRetired"
            >
              <mat-icon>{{ showRetired ? 'expand_less' : 'expand_more' }}</mat-icon>
              Previous numbers ({{ retiredCount }})
            </button>

            <!-- Only saved lines reach here, so this section is history in the literal sense. -->
            <ng-container *ngIf="showRetired">
              <ng-container *ngFor="let entry of entries.controls; let i = index">
                <ng-container *ngIf="!entry.get('isActive')?.value && isPersisted(i)">
                  <ng-container *ngTemplateOutlet="phoneRow; context: { $implicit: entry, index: i }"></ng-container>
                </ng-container>
              </ng-container>
            </ng-container>
          </div>

          <!--
            Declared inside `formArrayName` on purpose: `ngTemplateOutlet` resolves dependency
            injection from where a template is *declared*, not where it is inserted, so a row
            declared outside this container would fail to find its parent form directive.
          -->
          <ng-template #phoneRow let-entry let-i="index">
          <div
            [formGroupName]="i"
            class="twilio-config__row"
            [class.twilio-config__row--inactive]="!entry.get('isActive').value"
          >
            <div class="twilio-config__row-main">
              <mat-form-field appearance="outline" class="twilio-config__number">
                <mat-label>Phone Number</mat-label>
                <input
                  matInput
                  type="tel"
                  formControlName="phoneNumber"
                  placeholder="+15551234567"
                  matTooltip="Enter the phone number in the format +1XXXXXXXXXX"
                  (keypress)="onKeyPress($event)"
                  pattern="[0-9+]*"
                  inputmode="tel"
                />
                <mat-error *ngIf="entry.get('phoneNumber')?.hasError('required')"> Phone number is required </mat-error>
                <!--
                  One message per cause. The wording also names the countries: "+1" covers the US,
                  Canada and the Caribbean, and the backend's own 400 says "North American", which a
                  clinical lead read as "only US numbers are allowed" — the report behind this fix.
                -->
                <mat-error *ngIf="entry.get('phoneNumber')?.hasError('invalidTwilioPhone')">
                  Enter a US, Canadian or Caribbean number as +1XXXXXXXXXX, digits only.
                </mat-error>
                <mat-error *ngIf="entry.get('phoneNumber')?.hasError('invalidTwilioPhoneRange')">
                  The area code and the three digits after it must start with 2-9.
                </mat-error>
                <mat-error *ngIf="entry.get('phoneNumber')?.hasError('serviceCodeAreaCode')">
                  That area code is a service code (211, 311 … 911) and cannot be assigned to a line.
                </mat-error>
              </mat-form-field>


              <mat-slide-toggle
                class="twilio-config__toggle"
                formControlName="isActive"
                color="primary"
                (change)="onActiveToggled(i)"
              >
                Active
              </mat-slide-toggle>

              <div class="twilio-config__row-action">
                <button
                  *ngIf="!isPersisted(i)"
                  mat-icon-button
                  type="button"
                  aria-label="Discard this number"
                  matTooltip="Discard this number"
                  (click)="discardEntry(i)"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>



            <div *ngIf="isNonUsRegion(i)" class="twilio-config__warning">
              <mat-icon class="twilio-config__warning-icon">warning</mat-icon>
              <span>
                This is not a US number — it is from {{ nonUsPlaceName(i) }}. A +1 number is not
                necessarily American, and calls will go out from there.
              </span>
            </div>

            <div *ngIf="isNonGeographic(i)" class="twilio-config__warning">
              <mat-icon class="twilio-config__warning-icon">warning</mat-icon>
              <span>
                This number has no local presence in any state, so it may be toll-free. Toll-free numbers must not be
                used for patient-facing calls or texts.
              </span>
            </div>

          </div>
          </ng-template>
        </div>

        <!--
          Gated on `loadFailed`, not on the 403 alone: an empty list means "no lines" or "we never saw
          them", and only a successful load tells those apart. A 500 left this copy asserting the
          clinic had no numbers, which is the same lie on the more common failure.
        -->
        <p *ngIf="!entries.length && !loadFailed" class="twilio-config__empty">
          No phone numbers configured for this client yet.
        </p>

        <div *ngIf="loadFailed" class="twilio-config__warning">
          <mat-icon class="twilio-config__warning-icon">{{ loadDenied ? 'lock' : 'error_outline' }}</mat-icon>
          <!-- The wording is the only thing the reason decides: "ask for access" and "try again" are
               different actions, and offering the wrong one wastes the user's time. -->
          <span *ngIf="loadDenied">
            You do not have access to this client's phone numbers, so they cannot be shown or changed here. This clinic may well have
            numbers configured.
          </span>
          <span *ngIf="!loadDenied">
            This client's phone numbers could not be loaded, so they cannot be shown or changed here. This clinic may well have numbers
            configured — close the dialog and try again.
          </span>
        </div>

        <!-- Hidden whenever the load failed, refusal or not. Adding a line without having seen the
             existing ones cannot honour the one-active-line rule: `keepOnlyActive` only retires rows it
             loaded, so a save here can leave the clinic with two active lines. -->
        <button
          *ngIf="!loadFailed"
          mat-stroked-button
          type="button"
          class="twilio-config__add"
          (click)="addEntry()"
          [disabled]="isSaving"
        >
          <mat-icon>add</mat-icon>
          Add phone number
        </button>


        <!--
          Why the save was refused, kept where the failing action is and until the next attempt. The
          snack bar clears itself after a few seconds while this dialog stays open on purpose, which
          left the admin staring at the form that failed with the reason already gone.
        -->
        <div *ngIf="saveError" class="twilio-config__warning" role="alert">
          <mat-icon class="twilio-config__warning-icon">error_outline</mat-icon>
          <span>{{ saveError }}</span>
        </div>

        <mat-card-actions class="twilio-config__actions">
          <button mat-button type="button" (click)="onCancel()" [disabled]="isSaving">{{ loadFailed ? 'Close' : 'Cancel' }}</button>
          <!-- No Save when the load failed: there is nothing loaded to change, so the button could only
               ever close the dialog while looking like it saved something — or worse, persist a row
               added blind on top of lines it never loaded. -->
          <button *ngIf="!loadFailed" mat-raised-button color="primary" type="submit" [disabled]="configForm.invalid || isSaving">
            <span *ngIf="!isSaving">Save</span>
            <mat-spinner *ngIf="isSaving" diameter="20"></mat-spinner>
          </button>
        </mat-card-actions>
      </form>
    </mat-card-content>
  </mat-card>
</div>
